import {
  ClassificationEntry,
  ShadowTestDiscrepancy,
  ShadowTestLine,
  ShadowTestMatchSet,
} from "../lusca-search-api/generated-src";
import {
  getShadowTestResultStatus,
  ShadowTestResultResponseWithCompat,
} from "./shadowTestResultStatus";

const escapeCSV = (val?: string | number | boolean | null) => {
  if (val == null) return "";
  const s = String(val);
  return s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};

const row = (...cells: (string | number | boolean | undefined | null)[]) =>
  cells.map(escapeCSV).join(",");

const LINE_HEADERS = [
  "Environment",
  "Journal Side",
  "Company Code",
  "Location",
  "Cost Center",
  "Account Number",
  "Product Line",
  "Channel",
  "Project",
  "Functional Amount",
  "Functional Currency",
  "Local Amount",
  "Local Currency",
  "GL Line Description",
  "Usecase",
  "Set of Books",
  "Match Key",
  "Intent",
  "Classification",
];

const lineRow = (env: string, line: ShadowTestLine) =>
  row(
    env,
    line.journalSide,
    line.companyCode,
    line.location,
    line.costCenter,
    line.accountNumber,
    line.productLine,
    line.channel,
    line.project,
    line.functionalAmount,
    line.functionalCurrency || line.currency,
    line.localAmount,
    line.localCurrency || line.currency,
    line.glLineDescription,
    line.usecase,
    line.setOfBooks,
    line.matchKey,
    line.intent,
    line.classification
  );

export function exportShadowTestCSV(
  result: ShadowTestResultResponseWithCompat,
  classifications: Record<string, ClassificationEntry>
) {
  const lines: string[] = [];

  // Summary metadata
  const testDate = result.testTimestamp
    ? new Date(result.testTimestamp).toISOString()
    : "";

  lines.push("Summary");
  lines.push(row("Transaction ID", result.transactionId));
  lines.push(row("GL Product Group", result.glProductGroup));
  lines.push(row("Country Code", result.countryCode));
  lines.push(row("Payment Provider", result.paymentProvider));
  lines.push(row("Promo", result.isPromo ? "Yes" : "No"));
  lines.push(row("Order Date", result.orderDate));
  lines.push(row("Test Result", getShadowTestResultStatus(result)));
  if (result.resultStatusMessage?.trim()) {
    lines.push(row("Result Status Message", result.resultStatusMessage.trim()));
  }
  if (result.sourceAmountCheckPassed != null) {
    lines.push(
      row(
        "Source Amount Check",
        result.sourceAmountCheckPassed ? "PASS" : "FAIL"
      )
    );
  }
  lines.push(row("Test Date", testDate));
  lines.push(row("Total Gamma Lines", result.totalGammaLines));
  lines.push(row("Total Prod Lines", result.totalProdLines));
  lines.push("");

  // Usecase comparison
  lines.push("Usecases Matched");
  lines.push(row("Environment", "Usecases"));
  if (result.gammaUsecases?.length) {
    lines.push(row("Gamma", result.gammaUsecases.join("; ")));
  }
  if (result.prodUsecases?.length) {
    lines.push(row("Prod", result.prodUsecases.join("; ")));
  }
  if (result.jemUsecases?.length) {
    lines.push(row("JEM", result.jemUsecases.join("; ")));
  }
  lines.push("");

  // Match Sets
  if (result.matchSets?.length) {
    lines.push("Match Sets");
    lines.push(
      row(
        "Environment",
        "Match Key",
        "Account Number",
        "Amount",
        "Currency",
        "Classification",
        "Closed"
      )
    );
    for (const ms of result.matchSets) {
      lines.push(
        row(
          ms.environment,
          ms.matchKey,
          ms.accountNumber,
          ms.amount,
          ms.currency,
          ms.classification,
          ms.closed
        )
      );
    }
    lines.push("");
  }

  // Matched Lines
  if (result.matchedLines?.length) {
    lines.push("Matched USL Lines");
    lines.push(row(...LINE_HEADERS));
    for (const l of result.matchedLines) {
      lines.push(lineRow(l.environment || "matched", l));
    }
    lines.push("");
  }

  // Unique Lines
  const uniqueSections: [string, ShadowTestLine[] | undefined][] = [
    ["JEM Unique Lines", result.jemUniqueLines],
    ["Gamma Unique Lines", result.gammaUniqueLines],
    ["Prod Unique Lines", result.prodUniqueLines],
  ];
  for (const [title, items] of uniqueSections) {
    if (items?.length) {
      lines.push(title);
      lines.push(row(...LINE_HEADERS));
      for (const l of items) {
        lines.push(lineRow(l.environment || title.split(" ")[0], l));
      }
      lines.push("");
    }
  }

  // Discrepancies
  if (result.discrepancies?.length) {
    lines.push("Discrepancies");
    lines.push(
      row(
        "Usecase",
        "Field",
        "Gamma Value",
        "Prod Value",
        "JEM Value",
        "Intent",
        "Account Number",
        "Classification"
      )
    );
    for (const d of result.discrepancies) {
      lines.push(
        row(
          d.usecase,
          d.field,
          d.gammaValue,
          d.prodValue,
          d.jemValue,
          d.intent,
          d.accountNumber,
          d.classification
        )
      );
    }
    lines.push("");
  }

  // Account balance pivots
  const appendPivot = (
    title: string,
    pivot: { [key: string]: { [key: string]: number } } | undefined
  ) => {
    if (!pivot || Object.keys(pivot).length === 0) return;
    const accounts = Array.from(
      new Set(Object.values(pivot).flatMap((r) => Object.keys(r)))
    ).sort();
    lines.push(title);
    lines.push(row("GL Line Description", ...accounts, "Grand Total"));
    const colTotals: Record<string, number> = {};
    accounts.forEach((a) => (colTotals[a] = 0));
    for (const [glDesc, cells] of Object.entries(pivot).sort(([a], [b]) =>
      a.localeCompare(b)
    )) {
      const rowTotal = accounts.reduce((s, a) => s + (cells[a] ?? 0), 0);
      accounts.forEach(
        (a) => (colTotals[a] = (colTotals[a] ?? 0) + (cells[a] ?? 0))
      );
      lines.push(row(glDesc, ...accounts.map((a) => cells[a] ?? 0), rowTotal));
    }
    const grandTotal = accounts.reduce((s, a) => s + colTotals[a], 0);
    lines.push(
      row("Grand Total", ...accounts.map((a) => colTotals[a]), grandTotal)
    );
    lines.push("");
  };

  appendPivot("Account Balance Pivot - Gamma", result.gammaPivot);
  appendPivot("Account Balance Pivot - Prod", result.prodPivot);

  // Financial Amounts
  if (
    result.financialAmounts &&
    Object.keys(result.financialAmounts).length > 0
  ) {
    lines.push("Financial Amounts");
    lines.push(
      row(
        "Element",
        "Source",
        "Source Amount",
        "Source Curr",
        "Gamma USL Amount",
        "Gamma USL Curr",
        "Prod USL Amount",
        "Prod USL Curr"
      )
    );
    for (const [key, fa] of Object.entries(result.financialAmounts)) {
      const label = key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      lines.push(
        row(
          label,
          fa.source,
          fa.sourceAmount,
          fa.sourceCurr,
          fa.gammaUslAmount,
          fa.gammaUslCurr,
          fa.prodUslAmount,
          fa.prodUslCurr
        )
      );
    }
    if (result.sourceAmountCheckPassed != null) {
      lines.push(
        row(
          "Source Amount Check",
          result.sourceAmountCheckPassed ? "PASS" : "FAIL"
        )
      );
    }
    lines.push("");
  }

  // Classification codes legend
  const classEntries = Object.values(classifications);
  if (classEntries.length) {
    lines.push("Classification Codes");
    lines.push(row("Code", "Description"));
    for (const c of classEntries) {
      lines.push(row(c.code, c.description));
    }
  }

  return lines.join("\n");
}

export function downloadShadowTestCSV(
  result: ShadowTestResultResponseWithCompat,
  classifications: Record<string, ClassificationEntry>
) {
  const csv = exportShadowTestCSV(result, classifications);
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const filename = `shadow-test-${result.transactionId}-${
    result.glProductGroup || "unknown"
  }-${result.countryCode || "unknown"}-${ts}.csv`;

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
